import { useEffect, useState } from "react";
import {
  Stack,
  Typography,
  ImageList,
  ImageListItem,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import classes from "./Gallery.module.css";
import { trackGalleryImageView } from "../../utils/tracking";
import useDocumentMeta from "../../utils/useDocumentMeta";
import clientTreatment from "../../Assets/client_treatment.png";
import gallery1 from "../../Assets/gallery1.JPG";
import gallery2 from "../../Assets/gallery2.JPG";
import gallery3 from "../../Assets/gallery3.JPG";
import gallery4 from "../../Assets/gallery4.JPG";
import gallery5 from "../../Assets/gallery5.JPG";
import clientTreatment9 from "../../Assets/client_treatment_9.jpg";
import gallery6 from "../../Assets/gallery6.JPG";
import clientTreatment13 from "../../Assets/client_treatment_13.jpg";
import chairCloseup from "../../Assets/chair_closeup.jpg";
import raRoom from "../../Assets/ra_room.png";
import raTable from "../../Assets/ra_table.png";
import treatmentRoom from "../../Assets/treatment_room.png";
import medik8Products from "../../Assets/medik8_products.png";
import medik8Products2 from "../../Assets/medik8_2.jpg";

const galleryImages = [
  {
    id: 1,
    src: gallery4,
    alt: "Client relaxing after treatment at Radiance Aesthetics",
  },
  {
    id: 2,
    src: raRoom,
    alt: "Radiance Aesthetics private treatment room in Hindley",
  },
  {
    id: 3,
    src: gallery2,
    alt: "Client receiving advanced facial treatment at Radiance Aesthetics Wigan",
  },
  {
    id: 4,
    src: medik8Products,
    alt: "Medik8 professional skincare products used at Radiance Aesthetics",
  },
  {
    id: 5,
    src: clientTreatment13,
    alt: "Advanced facial treatment in progress at Radiance Aesthetics",
  },

  {
    id: 6,
    src: raTable,
    alt: "Relaxing ambience with candles at Radiance Aesthetics clinic",
  },
  {
    id: 7,
    src: gallery1,
    alt: "Advanced skin treatment in progress at our Hindley clinic",
  },
  {
    id: 8,
    src: gallery3,
    alt: "Dermalux LED light therapy session for skin rejuvenation",
  },
  {
    id: 9,
    src: clientTreatment9,
    alt: "Advanced facial being performed by qualified nurse",
  },

  {
    id: 10,
    src: chairCloseup,
    alt: "Treatment chair and equipment at Radiance Aesthetics Hindley clinic",
  },
  {
    id: 11,
    src: gallery5,
    alt: "Crystal facial therapy treatment at Radiance Aesthetics",
  },
  {
    id: 12,
    src: treatmentRoom,
    alt: "Calming treatment environment at Radiance Aesthetics Hindley",
  },
  {
    id: 13,
    src: clientTreatment,
    alt: "Client enjoying relaxing facial treatment in Wigan",
  },
  {
    id: 14,
    src: medik8Products2,
    alt: "Medik8 clinical skincare range available at Radiance Aesthetics",
  },

  {
    id: 15,
    src: gallery6,
    alt: "Client relaxing during facial treatment at our Wigan clinic",
  },
];

const Gallery = () => {
  useDocumentMeta(
    "Clinic Gallery | Radiance Aesthetics | Hindley | Wigan",
    "See inside Radiance Aesthetics — our treatment room, client moments and facial treatments in progress at our nurse-led clinic in Hindley, Wigan.",
  );

  const [aspectRatios, setAspectRatios] = useState({});
  const [loaded, setLoaded] = useState({});
  const [cols, setCols] = useState(3);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    trackGalleryImageView(index, galleryImages[index].alt);
  };

  const closeLightbox = () => setLightboxIndex(null);

  const showPrev = () =>
    setLightboxIndex((i) => {
      const next = i === 0 ? galleryImages.length - 1 : i - 1;
      trackGalleryImageView(next, galleryImages[next].alt);
      return next;
    });

  const showNext = () =>
    setLightboxIndex((i) => {
      const next = i === galleryImages.length - 1 ? 0 : i + 1;
      trackGalleryImageView(next, galleryImages[next].alt);
      return next;
    });

  useEffect(() => {
    const updateCols = () => {
      const width = window.innerWidth;
      if (width < 500) setCols(2);
      else if (width < 900) setCols(2);
      else setCols(3);
    };
    updateCols();
    window.addEventListener("resize", updateCols);
    return () => window.removeEventListener("resize", updateCols);
  }, []);

  useEffect(() => {
    let cancelled = false;

    galleryImages.forEach((image) => {
      const img = new Image();
      img.src = image.src;
      img.onload = () => {
        if (cancelled) return;
        const ratio = img.naturalHeight
          ? img.naturalWidth / img.naturalHeight
          : 1;
        setAspectRatios((prev) => {
          if (prev[image.id]) return prev;
          return { ...prev, [image.id]: ratio };
        });
      };
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className={classes.gallerySection}>
      <Stack
        sx={{
          maxWidth: "1108px",
          margin: "0 auto",
          alignItems: "center",
          padding: { xs: "20px 16px", md: "20px 24px" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Our Space
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            textAlign: "center",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1.5,
          }}
          variant="h1"
        >
          Gallery
        </Typography>
        <Typography
          sx={{
            color: "var(--color-text-light)",
            textAlign: "center",
            fontSize: "16px",
            mb: 5,
            maxWidth: "600px",
            lineHeight: 1.7,
            fontFamily: "var(--font-body)",
          }}
        >
          A glimpse into our treatment space, client moments, and the calming
          details that define the Radiance Aesthetics experience.
        </Typography>
        <ImageList
          sx={{
            width: "100%",
            maxWidth: "900px",
            height: "auto",
          }}
          variant="masonry"
          cols={cols}
          gap={12}
        >
          {galleryImages.map((image, index) => (
            <ImageListItem
              key={image.id}
              onClick={() => openLightbox(index)}
              sx={{
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden",
                aspectRatio: aspectRatios[image.id] || "1 / 1",
                backgroundColor: "var(--color-bg-cream)",
                cursor: "pointer",
              }}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                onLoad={() =>
                  setLoaded((prev) => ({ ...prev, [image.id]: true }))
                }
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  opacity: loaded[image.id] ? 1 : 0,
                  transition: "opacity 0.4s ease",
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Stack>

      <Dialog
        open={lightboxIndex !== null}
        onClose={closeLightbox}
        maxWidth="lg"
        PaperProps={{
          sx: { backgroundColor: "transparent", boxShadow: "none" },
        }}
      >
        {lightboxIndex !== null && (
          <Stack
            sx={{
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 1, sm: 2 },
            }}
          >
            <IconButton
              onClick={closeLightbox}
              aria-label="Close"
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                color: "#fff",
                backgroundColor: "rgba(0,0,0,0.4)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].alt}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                borderRadius: "8px",
                display: "block",
              }}
            />
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={2}
              sx={{ mt: 2 }}
            >
              <IconButton
                onClick={showPrev}
                aria-label="Previous image"
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                }}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <span style={{ color: "#fff", fontSize: "14px" }}>
                {lightboxIndex + 1} / {galleryImages.length}
              </span>
              <IconButton
                onClick={showNext}
                aria-label="Next image"
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                }}
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Dialog>
    </section>
  );
};

export default Gallery;
